/**
 * @coeldery/font-scale — 全站字級（根字級）調整
 *
 * 機制：改 `document.documentElement.style.fontSize`（**rem 縮放**）＋ 存 localStorage。
 *   → 宿主 CSS 只要用 `rem`（唔係寫死 px），全站（連 vendor 模組）一齊縮放。
 *
 * ⭐ 自足（P-027）：props／options 入；**唔綁 i18n**（按鈕文字由 `labels` 傳）；
 *    `FontScaleButtons` 用 inline style（零 CSS 依賴），顏色一律 CSS var ＋ fallback。
 */
import { useCallback, useEffect, useState, type CSSProperties } from 'react'

export interface FontScaleLevel {
  key: string
  /** 根字級（px；會寫入 `<html>` 嘅 font-size） */
  rootPx: number
}

/** 預設三段（14／16／18px）—— 宿主可自訂。 */
export const DEFAULT_LEVELS: readonly FontScaleLevel[] = [
  { key: 'small', rootPx: 14 },
  { key: 'normal', rootPx: 16 },
  { key: 'large', rootPx: 18 },
]

export interface FontScaleOptions {
  /** localStorage key（預設 `coeldery_font_scale`） */
  storageKey?: string
  levels?: readonly FontScaleLevel[]
  /** 預設級別 key（預設 `normal`） */
  defaultKey?: string
}

const DEF_STORAGE = 'coeldery_font_scale'
const DEF_KEY = 'normal'
/** ⭐ cosingery R5 適應：宿主 CSS 用嘅屬性名（`html[data-font-scale]`）。 */
const ATTR = 'data-font-scale'

function resolve(opts: FontScaleOptions = {}) {
  return {
    storageKey: opts.storageKey ?? DEF_STORAGE,
    levels: opts.levels ?? DEFAULT_LEVELS,
    defaultKey: opts.defaultKey ?? DEF_KEY,
  }
}

/** 讀目前級別 key（讀唔到／唔合法 → 預設）。 */
export function readFontScale(opts: FontScaleOptions = {}): string {
  const { storageKey, levels, defaultKey } = resolve(opts)
  try {
    const v = localStorage.getItem(storageKey)
    if (v && levels.some((l) => l.key === v)) return v
  } catch { /* localStorage 不可用（私隱模式）→ 用預設 */ }
  return defaultKey
}

/**
 * 套用級別：寫 `<html>` font-size（inline px）＋ `data-font-scale` 屬性 ＋ 存檔。
 *
 * ⚠️ **cosingery R5 就地適應**（2026-10-11；`decisions/052`／`tasks/J-ui-cosingery` **步 2**）：
 *   設計 v2 嘅 `tokens.css` 用 `html[data-font-scale="1…5"] { font-size }` 驅動字級，
 *   故此副本除咗原本嘅 inline font-size，**額外加寫 `data-font-scale` 屬性**（值 ＝ 級別 key）。
 *   兩者並存且等值 → 對「用屬性 CSS」同「純靠 inline」嘅宿主一樣成立。
 */
export function applyFontScale(key: string, opts: FontScaleOptions = {}): void {
  const { storageKey, levels, defaultKey } = resolve(opts)
  const lvl = levels.find((l) => l.key === key)
    ?? levels.find((l) => l.key === defaultKey)
    ?? levels[0]
  if (!lvl) return
  if (typeof document !== 'undefined') {
    document.documentElement.style.fontSize = `${lvl.rootPx}px`
    document.documentElement.setAttribute(ATTR, lvl.key)
  }
  try { localStorage.setItem(storageKey, lvl.key) } catch { /* ignore */ }
}

/** React hook：回 `{ level, levels, setLevel }`；掛載即套用、切換即生效。 */
export function useFontScale(opts: FontScaleOptions = {}) {
  const { levels } = resolve(opts)
  const [level, setLevelState] = useState<string>(() => readFontScale(opts))

  useEffect(() => { applyFontScale(level, opts) }, [level]) // eslint-disable-line react-hooks/exhaustive-deps

  const setLevel = useCallback((k: string) => setLevelState(k), [])
  return { level, levels, setLevel }
}

const btnBase: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--border-subtle, #e5e3dc)',
  background: 'var(--bg-input, #fff)',
  color: 'var(--text-primary, #2a2a2a)',
  fontSize: '0.875rem',
  cursor: 'pointer',
}
const btnActive: CSSProperties = {
  ...btnBase,
  borderColor: 'var(--accent, #0B4F4A)',
  background: 'var(--accent, #0B4F4A)',
  color: 'var(--text-on-primary, #fff)',
}

/**
 * 純顯示選擇器（inline style；零 CSS 依賴）。文字由 `labels`（key → 文字）傳入。
 * 配合 `useFontScale` 用：`const { level, levels, setLevel } = useFontScale(); <FontScaleButtons … />`
 */
export function FontScaleButtons({ levels = DEFAULT_LEVELS, value, onChange, labels, className }: {
  levels?: readonly FontScaleLevel[]
  value: string
  onChange: (key: string) => void
  labels: Record<string, string>
  className?: string
}) {
  return (
    <div className={className} role="group" style={{ display: 'flex', gap: 8 }} data-testid="font-scale">
      {levels.map((l) => (
        <button
          key={l.key}
          type="button"
          onClick={() => onChange(l.key)}
          aria-pressed={value === l.key}
          data-testid={`font-scale-${l.key}`}
          style={value === l.key ? btnActive : btnBase}
        >
          {labels[l.key] ?? l.key}
        </button>
      ))}
    </div>
  )
}
